/**
 * InlineAccessoryRows — S3 editable accessory rows inside the line-item form.
 *
 * Plan reference: PR-02b §1b.3
 * Wire: QB-LINE-line-item-accessories-FLOW.html (S3)
 *
 * Renders each accessory row on the current line item with:
 *  - qty stepper (free-typed AND stepped; Q1 + wire R4)
 *  - unit label (from snapshotted unit)
 *  - unit cost display (from snapshotted unit_cost in cents)
 *  - extended cost display (qty * unit_cost)
 *  - `Roll up` checkbox (default true) — label is EXACTLY "Roll up" everywhere (never "Include")
 *  - remove-x button
 *
 * All state is lifted — parent owns the accessories array on the line item.
 */

import React from 'react';
import { TrashIcon, MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Checkbox } from '../../ui';
import { ensureAccessoryRowIds } from '../../../quote-builder/accessoryRowIds';

/**
 * @param {Array}    accessories  Array of {id, accessory_id, name, unit, unit_cost, qty, rollUp}
 * @param {function} onChange     (nextAccessories) => void
 */
export default function InlineAccessoryRows({ accessories: rawAccessories = [], onChange }) {
  // IDI-442: act on ROW ids only. Rows reaching here without a unique id
  // (older drafts) get one first, so an edit can never match several rows.
  const accessories = ensureAccessoryRowIds(rawAccessories) || [];
  if (!accessories.length) return null;

  const update = (id, patch) => {
    onChange?.(accessories.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  };
  const remove = (id) => {
    onChange?.(accessories.filter((a) => a.id !== id));
  };

  return (
    <div className="flex flex-col gap-2">
      {accessories.map((acc) => {
        const qty = Number(acc.qty) || 0;
        const unitCostCents = Number(acc.unit_cost) || 0;
        const extendedCents = qty * unitCostCents;

        return (
          <div
            key={acc.id}
            className="flex items-center gap-2 p-2 rounded"
            style={{
              border: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-surface)',
            }}
          >
            {/* Name */}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" style={{ color: 'var(--color-text-default)' }}>
                {acc.name}
              </div>
              <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                ${(unitCostCents / 100).toFixed(2)} / {acc.unit || 'each'}
              </div>
            </div>

            {/* Qty stepper — free-typed + stepped (wire R4) */}
            <div
              className="flex items-center"
              style={{ border: '1px solid var(--color-border)', borderRadius: 4, height: 28 }}
            >
              <button
                type="button"
                onClick={() => update(acc.id, { qty: Math.max(0, qty - 1) })}
                className="px-1.5 h-full flex items-center justify-center"
                aria-label={`Decrease ${acc.name} quantity`}
                style={{ color: 'var(--color-text-muted)' }}
              >
                <MinusIcon className="h-3 w-3" />
              </button>
              <input
                type="number"
                min="0"
                step="1"
                value={qty}
                onChange={(e) => {
                  const raw = e.target.value;
                  if (raw === '') { update(acc.id, { qty: 0 }); return; }
                  const n = Math.max(0, Number(raw));
                  if (Number.isFinite(n)) update(acc.id, { qty: n });
                }}
                className="text-xs text-center outline-none"
                style={{
                  width: 36,
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: 'var(--color-text-default)',
                }}
                aria-label={`${acc.name} quantity`}
              />
              <button
                type="button"
                onClick={() => update(acc.id, { qty: qty + 1 })}
                className="px-1.5 h-full flex items-center justify-center"
                aria-label={`Increase ${acc.name} quantity`}
                style={{ color: 'var(--color-text-muted)' }}
              >
                <PlusIcon className="h-3 w-3" />
              </button>
            </div>

            {/* Extended cost */}
            <div className="text-sm font-medium" style={{ minWidth: 68, textAlign: 'right', color: 'var(--color-text-default)' }}>
              ${(extendedCents / 100).toFixed(2)}
            </div>

            {/* Roll up toggle — label is EXACTLY "Roll up" (never "Include").
                Uses the canonical Checkbox atom (R15: accent = brand red, not browser blue). */}
            <Checkbox
              checked={acc.rollUp !== false}
              onChange={(checked) => update(acc.id, { rollUp: checked })}
              label="Roll up"
              aria-label={`Roll up ${acc.name}`}
            />

            {/* Remove */}
            <button
              type="button"
              onClick={() => remove(acc.id)}
              className="p-1 rounded"
              aria-label={`Remove ${acc.name}`}
              style={{ color: 'var(--color-text-muted)' }}
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
